import React, { useMemo, useState } from 'react';
import { NimbusModal, NimbusModalContent } from '../components/NimbusModal';
import Select from 'react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { departmentsApi, peopleApi, usersApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { reactSelectStyles } from '../utils/reactSelectStyles';

type Department = {
  departmentId: string;
  name?: string;
  description?: string;
  parentDepartmentId?: string | null;
  headOfDepartment?: string | null;
  // Same personId space as headOfDepartment (not platform userIds) - who besides the head
  // may submit or reopen an evidence round for this team. Evidence submission form.
  delegatePersonIds?: string[];
  location?: string;
  // Who an AI Use Case submission auto-assigns to when its departmentId matches this
  // department - a real platform userId, not a People-register personId (the assignee needs
  // to log in and act on the assignment, which only a real user account can do).
  defaultReviewerId?: string | null;
};

type NimbusUser = {
  userId: string;
  name?: string;
  email?: string;
};

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
};

const getPersonLabel = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId).trim();
};

export const DepartmentsPage: React.FC = () => {
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();

  const { data: departments = [], isLoading, error } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  const { data: usersResponse } = useQuery<{ users?: NimbusUser[] }>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  });
  const users = usersResponse?.users || [];

  const peopleOptions = useMemo(
    () => people.map((p) => ({ value: p.personId, label: getPersonLabel(p) })),
    [people]
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [form, setForm] = useState({
    name: '',
    description: '',
    parentDepartmentId: '',
    headOfDepartment: '',
    delegatePersonIds: [] as string[],
    location: '',
    defaultReviewerId: '',
  });

  const toggleCollapsed = (departmentId: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(departmentId)) next.delete(departmentId);
      else next.add(departmentId);
      return next;
    });
  };

  const childrenByParentId = useMemo(() => {
    const map = new Map<string, Department[]>();
    departments.forEach((d) => {
      if (!d.parentDepartmentId) return;
      const siblings = map.get(d.parentDepartmentId) || [];
      siblings.push(d);
      map.set(d.parentDepartmentId, siblings);
    });
    return map;
  }, [departments]);

  const departmentIds = useMemo(() => new Set(departments.map((d) => d.departmentId)), [departments]);
  const rootDepartments = useMemo(
    () => departments.filter((d) => !d.parentDepartmentId || !departmentIds.has(d.parentDepartmentId)),
    [departments, departmentIds]
  );

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', description: '', parentDepartmentId: '', headOfDepartment: '', delegatePersonIds: [], location: '', defaultReviewerId: '' });
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (dept: Department) => {
    beginEdit(dept);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const parentOptions = useMemo(() => {
    return departments.filter((d) => d.departmentId !== editingId);
  }, [departments, editingId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        name: form.name,
        description: form.description,
        parentDepartmentId: form.parentDepartmentId || null,
        headOfDepartment: form.headOfDepartment || null,
        delegatePersonIds: form.delegatePersonIds,
        location: form.location,
        defaultReviewerId: form.defaultReviewerId || null,
      };

      if (editingId) {
        await departmentsApi.update(editingId, payload);
        return;
      }
      await departmentsApi.create(payload);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['departments'] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (dept: Department) => {
      await departmentsApi.delete(dept.departmentId);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (dept: Department) => {
    const ok = await swal.confirmDelete(dept.name || dept.departmentId);
    if (!ok) return;
    deleteMutation.mutate(dept);
  };

  const beginEdit = (dept: Department) => {
    setEditingId(dept.departmentId);
    setForm({
      name: dept.name || '',
      description: dept.description || '',
      parentDepartmentId: dept.parentDepartmentId || '',
      headOfDepartment: dept.headOfDepartment || '',
      delegatePersonIds: dept.delegatePersonIds || [],
      location: dept.location || '',
      defaultReviewerId: dept.defaultReviewerId || '',
    });
  };

  const personById = useMemo(() => {
    const map = new Map<string, Person>();
    people.forEach((p) => map.set(p.personId, p));
    return map;
  }, [people]);

  const userById = useMemo(() => {
    const map = new Map<string, NimbusUser>();
    users.forEach((u) => map.set(u.userId, u));
    return map;
  }, [users]);

  const renderDepartmentRows = (dept: Department, depth: number): React.ReactNode => {
    const head = dept.headOfDepartment ? personById.get(dept.headOfDepartment) : undefined;
    const reviewer = dept.defaultReviewerId ? userById.get(dept.defaultReviewerId) : undefined;
    const children = childrenByParentId.get(dept.departmentId) || [];
    const hasChildren = children.length > 0;
    const isCollapsed = collapsedIds.has(dept.departmentId);

    return (
      <React.Fragment key={dept.departmentId}>
        <tr style={depth > 0 ? { background: 'color-mix(in sRGB, var(--bs-body-color) 2%, var(--np-surface))' } : undefined}>
          <td className="np-row-title-static">
            <div className="d-flex align-items-center" style={{ paddingLeft: depth * 24 }}>
              {hasChildren ? (
                <button
                  type="button"
                  className="np-icon-btn me-1"
                  onClick={() => toggleCollapsed(dept.departmentId)}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  <i className={isCollapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'}></i>
                </button>
              ) : (
                <span style={{ display: 'inline-block', width: '1.5rem' }}></span>
              )}
              {depth > 0 && <i className="ri-corner-down-right-line text-muted me-1" style={{ fontSize: '0.8rem' }}></i>}
              <span>{dept.name || dept.departmentId}</span>
              {hasChildren && (
                <span className="np-chip np-tone-neutral np-tabular ms-2" style={{ fontSize: '0.7rem' }}>
                  {children.length} sub-dept{children.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </td>
          <td className="np-sub">{dept.description || '-'}</td>
          <td>{head ? getPersonLabel(head) : '-'}</td>
          <td>{reviewer ? (reviewer.name || reviewer.email) : <span className="np-sub">Not set</span>}</td>
          <td>
            <div className="d-flex gap-1">
              <button
                className="np-icon-btn"
                onClick={() => openEditModal(dept)}
                title="Edit"
              >
                <i className="ri-edit-line"></i>
              </button>
              <button
                className="np-icon-btn np-icon-btn-danger"
                onClick={() => handleDelete(dept)}
                disabled={deleteMutation.isPending}
                title="Delete"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          </td>
        </tr>
        {hasChildren && !isCollapsed && children.map((child) => renderDepartmentRows(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-building-line"
        title="Departments"
        colour="info"
        actions={
          <button className="np-btn np-btn-primary" onClick={openCreateModal}>
            <i className="ri-add-line"></i>Create Department
          </button>
        }
      />

      <div className="np-card">
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0"><i className="ri-error-warning-line"></i>Failed to load departments.</div>
          ) : departments.length === 0 ? (
            <div className="text-center py-5">
              <i className="ri-inbox-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">No departments found.</p>
            </div>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th style={{ width: '200px' }}>Head</th>
                    <th style={{ width: '200px' }}>Default Reviewer</th>
                    <th style={{ width: '180px' }}></th>
                  </tr>}>
                  {rootDepartments.map((d) => renderDepartmentRows(d, 0))}
            </NimbusTable>
          )}
        </div>
      </div>

      {showModal && (
        <NimbusModal>
          <NimbusModalContent
            title={editingId ? 'Edit Department' : 'Add Department'}
            onClose={closeModal}
            footer={
              <>
                <button type="button" className="np-btn np-btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="np-btn np-btn-primary"
                  onClick={() => { saveMutation.mutate(); closeModal(); }}
                  disabled={saveMutation.isPending || !form.name.trim()}
                >
                  {saveMutation.isPending ? 'Saving…' : editingId ? 'Save Changes' : 'Create Department'}
                </button>
              </>
            }
          >
            <div className="np-field">
              <label>Name</label>
              <input className="np-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="np-field">
              <label>Head of Department</label>
              <Select
                options={peopleOptions}
                value={peopleOptions.find((o) => o.value === form.headOfDepartment) || null}
                onChange={(opt) => setForm((f) => ({ ...f, headOfDepartment: opt?.value || '' }))}
                placeholder="Select person…"
                isClearable
                styles={reactSelectStyles()}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="np-field">
              <label>Delegates</label>
              <Select
                isMulti isClearable isSearchable
                options={peopleOptions}
                value={peopleOptions.filter((o) => form.delegatePersonIds.includes(o.value))}
                onChange={(opts) => setForm((f) => ({ ...f, delegatePersonIds: (opts || []).map((o) => o.value) }))}
                placeholder="Who else can submit or reopen for this team…"
                styles={reactSelectStyles()}
                menuPortalTarget={document.body}
              />
            </div>
            <div className="np-field">
              <label>Parent Department</label>
              <select
                className="np-select"
                value={form.parentDepartmentId}
                onChange={(e) => setForm((f) => ({ ...f, parentDepartmentId: e.target.value }))}
              >
                <option value="">None (Top-level)</option>
                {parentOptions.map((d) => (
                  <option key={d.departmentId} value={d.departmentId}>
                    {d.name || d.departmentId}
                  </option>
                ))}
              </select>
            </div>
            <div className="np-field">
              <label>Description</label>
              <textarea
                className="np-textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="np-field">
              <label>Default AI Use Case reviewer</label>
              <select
                className="np-select"
                value={form.defaultReviewerId}
                onChange={(e) => setForm((f) => ({ ...f, defaultReviewerId: e.target.value }))}
              >
                <option value="">No default - assign manually</option>
                {users.map((u) => (
                  <option key={u.userId} value={u.userId}>{u.name || u.email}</option>
                ))}
              </select>
              <p className="np-hint mt-1 mb-0">
                A use case in this department auto-assigns to this person when submitted for review.
              </p>
            </div>
          </NimbusModalContent>
        </NimbusModal>
      )}
    </div>
  );
};
